import React from 'react';
import User from 'records/User';
import moment from 'moment';
import WidgetBase from '../WidgetBase';
import './ConversationTimeline.styl';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import sumBy from 'lodash/sumBy';
import groupBy from 'lodash/groupBy';
import ConversationTimelineRow from './ConversationTimelineRow';
import TimeBlock from './TimeBlock';
import {
  calculatePosition,
  calculateNumHours,
  calculateSecondsPerPixel,
} from './ConversationTimelineUtils';
import CallDetails from 'records/CallDetails';
import {dailyTimeframeOptions} from '../Common';
import ConversationTimelineHeader from 'components/manager-dashboard/conversation-timeline/ConversationTimelineHeader';
import {ConversationTimelineExtra} from 'records/ManagerDashboard';
import {List, Map} from 'immutable';
import AuthStore from 'stores/AuthStore';
import classNames from 'classnames';
import Popover from 'material-ui/Popover';
import classnames from 'classnames';
import {HISTORY_ACTIONS, shouldUpdate} from 'components/StatsPage2.utils';

const BLOCK_WIDTH = 76; // maybe look at making this dynamic
const BLOCKS_TO_CAUSE_SCROLLING = 8;
const MIN_NUMBER_OF_BLOCKS = 3;
const TIMELINE_WIDTH = 670;

export default class ConversationTimeline extends WidgetBase {
  constructor(props) {
    super(props);

    this.updatePeriod = this.updatePeriod.bind(this);
    this.repClick = this.repClick.bind(this);
    this.searchFieldChanged = this.searchFieldChanged.bind(this);
    this.colorListUpdated = this.colorListUpdated.bind(this);
    this.getWidgetStateKey = this.getWidgetStateKey.bind(this);
    this.repRef = React.createRef(null);

    this.state = {
      showNoCallRepDialog: true,
      // a dummy state to force reload of noCallRepRef
      noCallRepRefTimestamp: 0,
      repContent: null,
    };
  }

  /**
   * event handler for the drop down
   * @param selectedPeriod
   */
  updatePeriod(selectedPeriod) {
    this.getWidgetData({
      widgetId: this.props.widgetId,
      // format the date using the browser's timezone so today is the browser's today,
      // same for yesterday, etc; otherwise moment will format with UTC timezone
      date: selectedPeriod.key.format('YYYY-MM-DD'),
      teamId: this.getTeamId(),
      widgetType: this.props.widgetType,
      selectedSearchFieldId: this.props.selectedSearchFieldId,
    });
  }

  getTeamId() {
    const {selectedTeam} = this.props;
    return selectedTeam ? selectedTeam.id : null;
  }

  getWidgetStateKey() {
    const authUserId = AuthStore.getState().user.id;
    return this.props.onBehalfOfManagerId || authUserId;
  }

  searchFieldChanged(selectedSearchFieldId) {
    if (selectedSearchFieldId) {
      this.getWidgetData({
        widgetId: this.props.widgetId,
        date: this.props.date,
        teamId: this.getTeamId(),
        widgetType: this.props.widgetType,
        selectedSearchFieldId: selectedSearchFieldId,
      });
    }

    const widgetState = Map(this.props.widgetState);
    const widgetExtraSettings = this.getExtraSettings();
    const updatedSettings = widgetExtraSettings.set('selectedSearchFieldId', selectedSearchFieldId);
    const updatedState = widgetState.set(this.getWidgetStateKey(), updatedSettings);
    this.setWidgetState(this.props.widgetId, updatedState);
  }

  colorListUpdated(colorList) {
    const widgetState = Map(this.props.widgetState);
    const widgetExtraSettings = this.getExtraSettings();
    const updatedSettings = widgetExtraSettings.set('colorList', colorList);
    const updatedState = widgetState.set(this.getWidgetStateKey(), updatedSettings);
    this.setWidgetState(this.props.widgetId, updatedState);
  }

  renderHeaders(numHours, startHour, secondsPerPixel) {
    // tack on the +1 to get the next hour so calls can bleed into it
    const blocks = [...Array(numHours + 1).keys()].map(i => {
      const headerTime = moment().hour(startHour + i);
      return (
        <TimeBlock secondsPerPixel={secondsPerPixel} key={i}>
          <span>{headerTime.format('hA')}</span>
        </TimeBlock>
      );
    });
    return <div className="call-header header-spacer">{blocks}</div>;
  }

  repClick(repInfo, e) {
    if (repInfo.active) {
      const state =
        this.props.location && this.props.location.pathname
          ? {
              pathname: this.props.location.pathname,
              action: HISTORY_ACTIONS.PUSH,
            }
          : null;
      this.showRepMetrics(7, repInfo.userId, repInfo.teamId, state);
    } else {
      e.stopPropagation();
    }
  }

  /*
  reps has this shape: {
        repInfo: repInfoMap[id],
        callInfo: callMap[id] || [],
        statsInfo: repStats[id] || {
          callCount: 0,
          durationTotal: 0,
          averageMinutes: 0
        }
      };
   */
  sortByNumCalls(repsPreSort) {
    return repsPreSort.sort((a, b) => {
      const diff = b.statsInfo.callCount - a.statsInfo.callCount;
      if (diff !== 0) return diff;
      return a.repInfo.getDisplayName().localeCompare(b.repInfo.getDisplayName());
    });
  }

  isNoCall(item) {
    // We don't check for no-call-rep if we are not sorting by num calls
    return this.props.sortByNumCalls && item.statsInfo.callCount === 0;
  }

  noCallClass(item) {
    return this.isNoCall(item) ? 'no-call' : '';
  }

  showNoCallsRepTooltip() {
    return (
      this.props.location && this.props.location.state && this.props.location.state.showTooltip
    );
  }

  /**
   * render the rep name column of the grid
   * @param orderedReps and the RepInfo  // used to maintain the proper order for rendering the repInfos
   * @param repInfo
   * @returns {*}
   */
  renderRepNames(orderedReps) {
    return orderedReps.map(item => {
      const noCall = this.isNoCall(item);
      const noCallClass = noCall ? 'no-call' : '';
      return (
        <div
          className={classNames(
            'rep-name pointer',
            item.repInfo.active ? '' : ' inactive',
            noCallClass
          )}
          key={item.repInfo.userId}
          onClick={e => this.repClick(item.repInfo, e)}
          onMouseLeave={() => {
            this.repRef.current = null;
            this.setState(state => ({...state, repContent: null}));
          }}
          onMouseEnter={e => {
            this.repRef.current = e.currentTarget;
            this.setState(state => ({
              ...state,
              repContent: noCall ? (
                <div className="no-call-reps-tooltip">
                  <div className="message">
                    These team members seem to be inactive during this period. Reach out to them to
                    see how you can help.
                  </div>
                </div>
              ) : (
                item.repInfo.getDisplayName()
              ),
            }));
          }}>
          <div className="rep-name-overflow">{item.repInfo.getDisplayName()}</div>
        </div>
      );
    });
  }

  /**
   * render the middle column of calls
   * @param orderedReps reps and their calls
   * @param numHours  number of hour blocks
   * @param firstCallDate   date of the first call in the callInfos
   * @param secondsPerPixel  calculcated number of seconds per pixel
   * @returns {*}
   */
  renderCallTimeline(orderedReps, numHours, firstCallDate, secondsPerPixel, searchFieldColorMap) {
    return orderedReps.map((item, i) => (
      <ConversationTimelineRow
        key={i}
        numHours={numHours}
        callInfo={item.callInfo}
        firstCallDate={firstCallDate}
        secondsPerPixel={secondsPerPixel}
        searchFieldColorMap={searchFieldColorMap}
        isNoCall={this.isNoCall(item)}
      />
    ));
  }

  /**
   * render the total and average minutes
   * @param orderedReps reps and their stats
   * @param repStats  calculated stats
   * @returns {*}
   */
  renderRepStats(orderedReps) {
    return orderedReps.map((item, i) => {
      return (
        <div className={classNames('stats-row', this.noCallClass(item))} key={i}>
          <div className="stats-number col1">{item.statsInfo.callCount}</div>
          <div className="stats-number col2">{item.statsInfo.averageMinutes.toFixed(2)}</div>
        </div>
      );
    });
  }

  /**
   * render the current time dashed line
   * @param firstCallDate
   * @param secondsPerPixel
   * @returns {*}
   */
  renderCurrentTime(firstCallDate, secondsPerPixel, selectedPeriod) {
    const rightNow = moment();
    if (rightNow.isSame(selectedPeriod.key, 'day')) {
      const left = calculatePosition(rightNow, firstCallDate, secondsPerPixel);
      return (
        <div
          className="current-time"
          style={{left}}
          ref={ref => (this.currentTimeElement = ref)}></div>
      );
    } else {
      return null;
    }
  }

  componentDidMount() {
    if (this.currentTimeElement && this.timelineElement) {
      this.timelineElement.scrollLeft = this.currentTimeElement.getBoundingClientRect().x;
    }
    const widgetExtraSettings = this.getExtraSettings();
    if (widgetExtraSettings.selectedSearchFieldId) {
      this.getWidgetData({
        widgetId: this.props.widgetId,
        date: this.props.date,
        teamId: this.getTeamId(),
        widgetType: this.props.widgetType,
        selectedSearchFieldId: widgetExtraSettings.selectedSearchFieldId,
      });
    }
    this.forceReAnchorNoCallRepPopover();
  }

  componentDidUpdate(prevProps) {
    if (shouldUpdate(prevProps, this.props, true, false)) {
      this.getWidgetData({
        widgetId: this.props.widgetId,
        date: this.props.date,
        teamId: this.getTeamId(),
        widgetType: this.props.widgetType,
        selectedSearchFieldId: this.props.selectedSearchFieldId,
      });
    }
    this.forceReAnchorNoCallRepPopover();
  }

  forceReAnchorNoCallRepPopover() {
    // add one more re-render to each update cycle so that the noCallsRep popover, if any, can find its anchor
    if (this.props.sortByNumCalls && this.showNoCallsRepTooltip()) {
      const now = moment().unix();
      if (!this.state.noCallRepRefTimestamp || now - this.state.noCallRepRefTimestamp > 5000) {
        const newState = this.state;
        newState.noCallRepRefTimestamp = now;
        this.setState(newState);
      }
    }
  }

  /**
   * helper to initialize the ConversationTimelineExtra settings.
   *
   * ConversationTimelineExtra is a map of managerId to selectedFieldId and colorList
   * {
   *   managerId: {
   *     selectedSearchFieldId: 86,
   *     colorList: [{value: 88, colorIndex:0}, {value: 89, colorIndex:1}]
   *   }
   * }
   */
  getExtraSettings() {
    const widgetState = Map(this.props.widgetState);
    return new ConversationTimelineExtra(widgetState.get(this.getWidgetStateKey()));
  }

  render() {
    const {
      calls,
      repInfo,
      date,
      searchFields,
      selectedSearchFieldValues,
      sortByNumCalls = false,
    } = this.props;
    const searchFieldSelect = searchFields.map(searchField => {
      return {value: searchField.searchFieldId, label: searchField.outputName};
    });

    const searchFieldOptions = selectedSearchFieldValues.map(option => {
      return {value: option.id, label: option.display};
    });

    const widgetExtraSettings = this.getExtraSettings();
    const colorList = List(widgetExtraSettings.colorList);
    const searchOptionsMap = keyBy(selectedSearchFieldValues, 'id');
    // TODO: map callback should always return the value
    // eslint-disable-next-line
    const colorMap = colorList.map(color => {
      const searchOption = searchOptionsMap[color.value];
      if (searchOption) {
        return {
          value: searchOption.elasticsearchValue.toLowerCase(),
          colorIndex: color.colorIndex,
        };
      }
    });
    const searchFieldColorMap = keyBy(colorMap.toArray(), 'value');
    const timeframeOptions = dailyTimeframeOptions(7);
    const selectedPeriod = timeframeOptions.find(x => x.key.isSame(date)) || timeframeOptions[0];

    // Map the CallTimelineInfo object to a CallDetails.  I think we need to visit modifying the BE to produce an
    // object that more closely matches and fills out more properties.
    // https://execvision.myjetbrains.com/youtrack/issue/EV-8442
    let numHours, startHour, firstCallDate;
    const sortedCalls = calls
      .map(
        call =>
          new CallDetails({
            description: call.callDescription,
            timestamp: moment(call.date),
            durationSeconds: call.duration,
            callerId: call.repUserId,
            id: call.id,
            callStage: call.callStage ? call.callStage.toLowerCase() : '',
          })
      )
      .sort((a, b) => a.timestamp.diff(b.timestamp));

    if (calls.length > 0) {
      firstCallDate = moment(sortedCalls[0].timestamp).startOf('hour');
      const lastCall = sortedCalls[sortedCalls.length - 1];
      const lastCallDate = moment(lastCall.timestamp)
        .add(lastCall.durationSeconds, 'seconds')
        .startOf('hour');
      const nowHour = moment().startOf('hour');

      // it's not supposed to be possible to have calls in the future, but I'll code it this way just in case
      // I want to take the greater of the lastCall and the current hour, but only if we are viewing today's calls
      const lastCallHour = !lastCallDate.isSame(nowHour, 'day')
        ? lastCallDate
        : lastCallDate > nowHour
        ? lastCallDate
        : nowHour;
      startHour = firstCallDate.hour();
      numHours = calculateNumHours(firstCallDate, lastCallHour, MIN_NUMBER_OF_BLOCKS);
    } else {
      // There are no calls, but we still need to make up some values so the hours grid will show properly
      const now = moment();
      const hour = now.hour();
      // show the current hour and the three hours before it
      startHour = hour >= 3 ? hour - 3 : 0;
      numHours = 3;
      firstCallDate = now.startOf('day').add(startHour, 'hours');
    }

    // dynamic block width based on the number of hours we are going to render.  If we are going to render more than
    // BLOCKS_TO_CAUSE_SCROLLING then use the static BLOCK_WIDTH and scroll the view port
    const blockWidth =
      numHours < BLOCKS_TO_CAUSE_SCROLLING ? TIMELINE_WIDTH / (numHours + 1) : BLOCK_WIDTH;

    const secondsPerPixel = calculateSecondsPerPixel(blockWidth);

    // since I'm rendering the rep names and their timelines in separate columns of the grid I want to
    // have a consistent order for the reps and calls.  I'm going to use this repIds array to maintain the same
    // order of rendering
    const repIds = repInfo.map(item => item.userId);

    const repInfoMap = keyBy(
      repInfo.map(item => new User(item)),
      item => item.userId
    );

    // group the calls by user id and then calculate their totals and averages
    const callMap = groupBy(sortedCalls, item => item.callerId);

    const repStats = mapValues(callMap, value => {
      const durationTotal = sumBy(value, item => item.durationSeconds);
      const callCount = value.length;
      const averageMinutes = durationTotal / 60 / callCount;
      return {durationTotal, callCount, averageMinutes};
    });

    // make sure all the lists are in the same order, and then we may sort if sortByNumCalls is true
    const orderedRepsPreSort = repIds.map(id => {
      return {
        repInfo: repInfoMap[id],
        callInfo: callMap[id] || [],
        statsInfo: repStats[id] || {
          callCount: 0,
          durationTotal: 0,
          averageMinutes: 0,
        },
      };
    });
    const orderedReps = sortByNumCalls
      ? this.sortByNumCalls(orderedRepsPreSort)
      : orderedRepsPreSort;
    return (
      <>
        <div className="widget conversation-timeline-widget">
          <ConversationTimelineHeader
            {...this.props}
            searchFieldSelect={searchFieldSelect}
            searchFieldOptions={searchFieldOptions}
            updatePeriod={this.updatePeriod}
            selectedPeriod={selectedPeriod}
            timeframeOptions={timeframeOptions}
            searchFieldChanged={this.searchFieldChanged}
            colorList={colorList}
            colorListUpdated={this.colorListUpdated}
          />
          <div className="content">
            <div className="rep-names">
              <div className="header-spacer"></div>
              {this.renderRepNames(orderedReps)}
            </div>
            <div className="timeline-wrapper" ref={ref => (this.timelineElement = ref)}>
              <div className="timeline color-container">
                {this.renderHeaders(numHours, startHour, secondsPerPixel)}
                {this.renderCallTimeline(
                  orderedReps,
                  numHours,
                  firstCallDate,
                  secondsPerPixel,
                  searchFieldColorMap
                )}
                {this.renderCurrentTime(firstCallDate, secondsPerPixel, selectedPeriod)}
              </div>
            </div>
            <div className="timeline-stats">
              <div className="stats-row header-spacer">
                <div className="stats-number col1">#</div>
                <div className="average-talk col2">Avg Minutes Per Call</div>
              </div>
              {this.renderRepStats(orderedReps)}
            </div>
          </div>
        </div>
        <Popover
          open={!!this.repRef.current}
          className="destyle-popover todays-conversations-no-calls-rep-popover"
          useLayerForClickAway={false}
          anchorEl={this.repRef.current}
          anchorOrigin={{
            horizontal: 'middle',
            vertical: 'top',
          }}
          targetOrigin={{horizontal: 'middle', vertical: 'bottom'}}>
          <div className={classnames('tooltip-container', 'tail')}>
            <div className="ev-tooltip">
              <div className="tooltip-content">{this.state.repContent}</div>
            </div>
          </div>
        </Popover>
      </>
    );
  }
}
